import React, { useState, useEffect } from 'react';
import { Layers, ArrowRight, Loader2 } from 'lucide-react';
import api from '../services/api';

/**
 * SubGroupsTooltip - Rich tooltip showing subgroups grouped by main group
 * with merged row headers, parts count, and visual density bars
 */
const SubGroupsTooltip = ({ onSubGroupClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Get all subgroups
      const response = await api.get('/api/v1/taxonomy/subgroups-all');
      const allSubgroups = response.data || [];
      
      // Group by main group
      const groupedByMain = {};
      let maxParts = 1;
      let emptyCount = 0;
      
      allSubgroups.forEach(sg => {
        const mainCode = sg.main_group_code;
        if (!groupedByMain[mainCode]) {
          groupedByMain[mainCode] = {
            code: mainCode,
            name: sg.main_group_name || mainCode,
            subgroups: []
          };
        }
        groupedByMain[mainCode].subgroups.push(sg);
        if ((sg.part_count || 0) > maxParts) {
          maxParts = sg.part_count;
        }
        if ((sg.part_count || 0) === 0) {
          emptyCount++;
        }
      });

      // Sort subgroups within each main group by part count
      Object.values(groupedByMain).forEach(group => {
        group.subgroups.sort((a, b) => (b.part_count || 0) - (a.part_count || 0));
      });

      // Sort main groups by total parts
      const sortedGroups = Object.values(groupedByMain).sort((a, b) => {
        const aTotal = a.subgroups.reduce((sum, sg) => sum + (sg.part_count || 0), 0);
        const bTotal = b.subgroups.reduce((sum, sg) => sum + (sg.part_count || 0), 0);
        return bTotal - aTotal;
      });

      setData({
        groups: sortedGroups.slice(0, 5), // Show top 5 main groups
        maxParts,
        emptyCount,
        total: allSubgroups.length
      });
    } catch (error) {
      console.error('Failed to load subgroups data:', error);
      setData({ groups: [], maxParts: 1, emptyCount: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-white" />
            <span className="font-semibold text-white text-sm">Sub Groups</span>
          </div>
        </div>
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-96 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-white" />
            <span className="font-semibold text-white text-sm">Sub Groups</span>
          </div>
          <span className="text-orange-200 text-xs">{data.total} total</span>
        </div>
      </div>

      {/* Table */}
      <div className="max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0 z-10">
            <tr className="border-b border-slate-200">
              <th className="text-left px-3 py-2 font-medium text-slate-500">Main / Sub Group</th>
              <th className="text-right px-3 py-2 font-medium text-slate-500">Parts</th>
              <th className="px-3 py-2 font-medium text-slate-500 w-16">Density</th>
            </tr>
          </thead>
          <tbody>
            {data.groups.map((mainGroup) => (
              <React.Fragment key={mainGroup.code}>
                {/* Main Group Header Row */}
                <tr className="bg-gradient-to-r from-slate-50 to-slate-100">
                  <td colSpan={3} className="px-3 py-2 font-semibold text-slate-700 border-b border-slate-200">
                    <span className="font-mono text-purple-600">{mainGroup.code}</span>
                    <span className="ml-1.5">{mainGroup.name}</span>
                    <span className="text-slate-400 font-normal ml-2">({mainGroup.subgroups.length})</span>
                  </td>
                </tr>
                
                {/* Top 2 Subgroups */}
                {mainGroup.subgroups.slice(0, 2).map((sg) => {
                  const density = data.maxParts > 0 ? ((sg.part_count || 0) / data.maxParts) * 100 : 0;
                  return (
                    <tr 
                      key={`${mainGroup.code}-${sg.subgroup_code}`}
                      onClick={() => onSubGroupClick?.(mainGroup.code, sg.subgroup_code)}
                      className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-2 pl-6 text-slate-600 truncate max-w-[200px]">
                        {sg.name}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-700">
                        {(sg.part_count || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-orange-500 rounded-full transition-all"
                            style={{ width: `${Math.max(density, 2)}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                
                {/* +N more indicator */}
                {mainGroup.subgroups.length > 2 && (
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-1.5 pl-6 text-slate-400 text-xs">
                      +{mainGroup.subgroups.length - 2} more subgroups
                    </td>
                    <td className="px-3 py-1.5 text-right text-slate-400">
                      {mainGroup.subgroups.slice(2).reduce((sum, sg) => sum + (sg.part_count || 0), 0)}
                    </td>
                    <td></td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Insight Footer */}
      <div className="px-3 py-2 bg-amber-50 border-t border-amber-100">
        <p className="text-xs text-amber-800">
          <span className="font-semibold">💡</span> Showing top 2 per group • {data.emptyCount} empty subgroups
        </p>
      </div>

      {/* View All Link */}
      <div className="px-3 py-2 bg-slate-50 border-t border-slate-200">
        <a 
          href="/taxonomy" 
          className="text-xs text-orange-600 hover:text-orange-800 font-medium flex items-center gap-1 justify-center"
        >
          View All Sub Groups
          <ArrowRight className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
};

export default SubGroupsTooltip;
